
import React, { useEffect, useMemo, useRef, useState } from "react";
import '../../styles/weather-layer-dropdown.css';

export type WeatherLayerItem = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
};

export type WeatherLayerDropdownProps = {
  title?: string;
  items: WeatherLayerItem[];
  selectedId?: string;                 // controlled
  onChange?: (id: string) => void;     // emits selected layer id
  open?: boolean;                      // controlled open state
  onOpenChange?: (open: boolean) => void;
  className?: string;
};

const Chevron: React.FC<{ up?: boolean }> = ({ up }) => (
  <span
    className={`wl__chevron ${up ? "wl__chevron--up" : "wl__chevron--down"}`}
    aria-hidden="true"
  />
);

/** Focus management for listbox */
function useListFocus(count: number) {
  const [idx, setIdx] = useState(0);
  const clamp = (n: number) => Math.max(0, Math.min(n, count - 1));
  return {
    idx,
    setIdx,
    next: () => setIdx(i => clamp(i + 1)),
    prev: () => setIdx(i => clamp(i - 1)),
    first: () => setIdx(0),
    last: () => setIdx(count - 1),
  };
}

export const WeatherLayerDropdown: React.FC<WeatherLayerDropdownProps> = ({
  title = "Weather Layer",
  items,
  selectedId,
  onChange,
  open,
  onOpenChange,
  className,
}) => {
  const [internalOpen, setInternalOpen] = useState(true);
  const isControlledOpen = typeof open === "boolean";
  const isOpen = isControlledOpen ? open : internalOpen;

  const setOpen = (val: boolean) => {
    if (isControlledOpen) onOpenChange?.(val);
    else setInternalOpen(val);
  };

  const selectedIndex = useMemo(() => {
    const i = items.findIndex(i => i.id === selectedId);
    return i >= 0 ? i : 0;
  }, [items, selectedId]);

  const { idx, setIdx, next, prev, first, last } = useListFocus(items.length);
  useEffect(() => setIdx(selectedIndex), [selectedIndex, setIdx]);

  const listRef = useRef<HTMLUListElement>(null);

  const toggle = () => setOpen(!isOpen);

  const handleSelect = (id: string, disabled?: boolean) => {
    if (disabled) return;
    onChange?.(id);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    if (!isOpen) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault(); next();
        break;
      case "ArrowUp":
        e.preventDefault(); prev();
        break;
      case "Home":
        e.preventDefault(); first();
        break;
      case "End":
        e.preventDefault(); last();
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        const target = items[idx];
        if (target) handleSelect(target.id, target.disabled);
        break;
      case "Escape":
        e.preventDefault(); setOpen(false);
        break;
    }
  };

  return (
    <div className={`wl ${className ?? ""}`} aria-expanded={isOpen}>
      <button
        className="wl__header"
        type="button"
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="wl__title">{title}</span>
        <Chevron up={isOpen} />
      </button>

      <div
        className={`wl__panel ${isOpen ? "is-open" : ""}`}
        role="region"
        aria-label={`${title} options`}
      >
        <ul
          className="wl__list"
          role="listbox"
          aria-activedescendant={items[idx]?.id}
          tabIndex={0}
          ref={listRef}
          onKeyDown={onKeyDown}
        >
          {items.map((item, i) => {
            const isSelected = item.id === selectedId;
            const isActive = i === idx;
            return (
              <li key={item.id} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  className={`wl__item ${isSelected ? "is-selected" : ""} ${item.disabled ? "is-disabled" : ""} ${isActive ? "is-active" : ""}`}
                  onClick={() => handleSelect(item.id, item.disabled)}
                  disabled={item.disabled}
                >
                  <span className="wl__icon">{item.icon}</span>
                  <span className="wl__label">{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
